import { DefaultError } from '@tanstack/query-core'
import {
  queryOptions,
  useQueries,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query'
import axios from 'axios'
import { useCallback } from 'react'
import { CreditCardStatus } from 'src/models/creditCard.models'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { statisticsQueryKeys } from 'src/queries/queries.constants'
import { getRequestConfig } from 'src/queries/requestConfig'
import {
  postStatistics,
  StatisticsResponse,
} from 'src/queries/statistics/statistics.network'
import {
  StatisticsKeys,
  StatisticsRequestsBody,
} from 'src/queries/statistics/statistics.queries.constants'
import {
  CreditCardStatisticRequest,
  StatisticAggregateType,
  StatisticModelType,
} from 'src/requests/statistics.request'

/**
 * Deliberate freshness window for statistics counts (EX-39745).
 *
 * The global default staleTime is 0, which made every mount of every consumer
 * refire its POSTs (the Update/Create VC forms alone mount 12/17 stat
 * queries). Statistics back badges, gating, and picker counts — values that
 * do not need sub-30-second freshness — and event-driven refresh happens by
 * invalidating a stat's key, which refetches mounted observers immediately
 * regardless of this window.
 */
export const STATISTICS_STALE_TIME_MS = 30_000

export const statisticsQueries = {
  /**
   * One statistic as its own query: a single-entry POST /statistics cached
   * under `statisticsQueryKeys.list([key])`, so each stat dedupes across
   * consumers and can be invalidated individually. Per-stat queries (not one
   * batched request) are the deliberate transport: batching would fragment
   * the cache by key-set and make surgical cross-feature invalidation
   * impossible.
   */
  single: (statisticKey: StatisticsKeys) =>
    queryOptions({
      queryKey: statisticsQueryKeys.list([statisticKey]),
      queryFn: () => postStatistics([StatisticsRequestsBody[statisticKey]]),
      select: (data: StatisticsResponse) => data.statistics[0]?.value,
      staleTime: STATISTICS_STALE_TIME_MS,
    }),
}

type StatisticsMappedResults<T extends readonly StatisticsKeys[]> = {
  [K in T[number]]: UseQueryResult<number | undefined>
}
type UseStatisticsQueryResult<T extends readonly StatisticsKeys[]> = {
  results: StatisticsMappedResults<T>
  anyLoading: boolean
}

interface UseQueryStatisticsOptions<T extends readonly StatisticsKeys[]> {
  statisticsKeys: T
  enabled?: boolean
}

/**
 * Fetches a list of statistic keys as separate per-key queries (one
 * single-entry POST /statistics each — see statisticsQueries.single) and maps
 * the results back onto the keys. A composed hook is justified here because
 * it combines N queries; for a single stat, consume
 * `useQuery(statisticsQueries.single(key))` directly.
 */
export const useQueryStatistics = <T extends readonly StatisticsKeys[]>(
  options: UseQueryStatisticsOptions<T>,
): UseStatisticsQueryResult<T> => {
  const combineStatistics = useCallback(
    (
      results: Array<UseQueryResult<number | undefined>>,
    ): UseStatisticsQueryResult<T> => {
      const mappedResults = results.reduce((acc, result, i) => {
        return {
          ...acc,
          [options.statisticsKeys[i]]: result,
        }
      }, {} as StatisticsMappedResults<T>)

      return {
        results: mappedResults,
        anyLoading: results.some((result) => result.isLoading),
      }
    },
    [options.statisticsKeys],
  )

  return useQueries({
    queries: options.statisticsKeys.map((statisticKey) => ({
      ...statisticsQueries.single(statisticKey),
      ...(options.enabled === false ? { enabled: false as const } : {}),
    })),
    combine: combineStatistics,
  })
}

/**
 * Query key for the onboarding drop-off force-register check (EX-40009).
 *
 * It extends the shared statistics key for `CreditCardsOrgSourceCount` with the
 * per-page-visit `cacheToken`, so React Query treats every visit as a distinct
 * fetch (rather than serving its own cached/deduped value) and the busted
 * request — carrying `query.cacheTimestamp` — actually reaches the network.
 * Exported so tests can assert the key varies with the token.
 */
export const onboardingDropoffSourceCountQueryKey = (cacheToken: string) =>
  [
    ...statisticsQueryKeys.list([StatisticsKeys.CreditCardsOrgSourceCount]),
    cacheToken,
  ] as const

/**
 * EX-40009 — focused query for the onboarding drop-off force-register check.
 *
 * The org source-card count is served from a stale extend-api Redis cache (key
 * derived from the request's `toString()`), so a freshly-onboarded owner can
 * read `0` for up to the TTL and get bounced back to `/onboarding/card`. The
 * shared `useQueryStatistics` can't fix this: its query key is FIXED per stat,
 * so a body-only `cacheTimestamp` change would be served from the React Query
 * cache and never refetch (the iOS author's explicit web note — see PR #2926).
 *
 * This query folds the per-visit `cacheToken` into BOTH layers:
 *   - the query KEY, so React Query actually sends the request each visit; and
 *   - the request body's `query.cacheTimestamp`, so the backend Redis cache
 *     misses and recomputes the count live from the database.
 *
 * Only `CreditCardsOrgSourceCount` is busted — the shared
 * `StatisticsRequestsBody` constant is left untouched, so every other stat
 * keeps the shared cached path (surgical busting, mirroring extend-ios #2926).
 * Returns a `UseQueryResult<number | undefined>` (the count) so the mapping in
 * `useOnboardingDropoffStatus` stays a thin destructure onto the existing
 * `shouldRedirect` / `isResumingOnboarding` decision logic.
 */
export const useQueryOnboardingDropoffSourceCount = ({
  cacheToken,
  enabled,
}: {
  cacheToken: string
  enabled: boolean
}): UseQueryResult<number | undefined> => {
  return useQuery<StatisticsResponse, DefaultError, number | undefined>({
    queryKey: onboardingDropoffSourceCountQueryKey(cacheToken),
    queryFn: async () => {
      // Copy the shared stat definition and stamp the busting token onto its
      // query — never mutate the shared `StatisticsRequestsBody` constant. The
      // `as` narrows the `Record<…, StatisticRequest>` union lookup to the
      // credit-card variant (the only one whose query carries `cacheTimestamp`).
      const base = StatisticsRequestsBody[
        StatisticsKeys.CreditCardsOrgSourceCount
      ] as CreditCardStatisticRequest
      const bustedStatistic: CreditCardStatisticRequest = {
        ...base,
        query: { ...base.query, cacheTimestamp: cacheToken },
      }
      return postStatistics([bustedStatistic])
    },
    select: (data) => data?.statistics[0]?.value,
    enabled,
  })
}

interface CreditCardStatisticsResponse {
  activeBudgetCount: number
  creditCardIds: string[]
  statistics: Array<{ value: number }>
}

export const useQueryCreditCardVCNStatistics = (
  creditCardIds: string[],
  type: StatisticModelType,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['creditCardStatistics', 'vcn'],
    queryFn: async () => {
      const response = await axios.post<CreditCardStatisticsResponse>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: type,
            query: {
              status: VirtualCardStatus.Active,
              creditCardId: ccId,
              all: true,
            },
          })),
        },
        getRequestConfig(),
      )
      return {
        ...response.data,
        creditCardIds,
        statistics: response.data.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [creditCardIds[index]]: curr.value,
          }),
          {} as Record<string, number>,
        ),
      }
    },
    enabled,
  })
}

export const useQueryCreditCardBudgetsStatistics = (
  creditCardIds: string[],
  type: StatisticModelType,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['creditCardStatistics', 'budgets'],
    queryFn: async () => {
      const response = await axios.post<CreditCardStatisticsResponse>(
        `/statistics`,
        {
          statistics: creditCardIds.map((ccId) => ({
            agg: StatisticAggregateType.Count,
            type: type,
            query: {
              status: CreditCardStatus.Active,
              parentCreditCard: ccId,
              all: true,
            },
          })),
        },
        getRequestConfig(),
      )
      return {
        ...response.data,
        creditCardIds,
        statistics: response.data.statistics.reduce(
          (acc, curr, index) => ({
            ...acc,
            [creditCardIds[index]]: curr.value,
          }),
          {} as Record<string, number>,
        ),
      }
    },
    enabled,
  })
}
